import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { dramas, dramaRegions, dramaTypes, dramaYears } from "@/mocks/dramas";
import DramaCard from "@/components/feature/DramaCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/feature/EmptyState";
import CardSkeleton from "@/components/feature/CardSkeleton";
import Pagination from "@/components/feature/Pagination";
import usePageMeta from "@/hooks/usePageMeta";

const PAGE_SIZE = 12;

const sortOptions = [
  { value: "updated", label: "按更新时间" },
  { value: "hot", label: "按热度" },
  { value: "year", label: "按首播时间" },
];

type SortValue = "updated" | "hot" | "year";

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialType = searchParams.get("type");
  const initialSort = (searchParams.get("sort") as SortValue) || "updated";

  const [types, setTypes] = useState<string[]>(initialType ? [initialType] : []);
  const [regions, setRegions] = useState<string[]>([]);
  const [years, setYears] = useState<number[]>([]);
  const [sort, setSort] = useState<SortValue>(initialSort);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const activeTypeName = types.length === 1 ? types[0] : "";

  usePageMeta(
    `${activeTypeName || "全部"}电视剧合集 - 善良的秘书`,
    `善良的秘书为你整理${activeTypeName || "各类热门"}电视剧合集，支持按类型、地区、年份筛选与热度排序，快速找到想看的剧集。`
  );

  const filtered = useMemo(() => {
    let list = dramas.filter((d) => {
      const matchType = types.length === 0 || d.types.some((t) => types.includes(t));
      const matchRegion = regions.length === 0 || regions.includes(d.region);
      const matchYear = years.length === 0 || years.includes(d.year);
      return matchType && matchRegion && matchYear;
    });

    list = [...list].sort((a, b) => {
      if (sort === "hot") return b.hot - a.hot;
      if (sort === "year") return b.year - a.year;
      return a.updatedAt < b.updatedAt ? 1 : -1;
    });
    return list;
  }, [types, regions, years, sort]);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 320);
    return () => clearTimeout(timer);
  }, [types, regions, years, sort, page]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [types, regions, years, sort]);

  const toggle = <T,>(list: T[], value: T, setter: (v: T[]) => void) => {
    setter(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  };

  const resetFilters = () => {
    setTypes([]);
    setRegions([]);
    setYears([]);
    setSort("updated");
    setSearchParams({}, { replace: true });
  };

  const hasFilters = types.length + regions.length + years.length > 0;

  const syncType = (next: string[]) => {
    setTypes(next);
    const params: Record<string, string> = {};
    if (next.length === 1) params.type = next[0];
    if (sort !== "updated") params.sort = sort;
    setSearchParams(params, { replace: true });
  };

  return (
    <div className="w-full px-4 md:px-6 py-8 md:py-12">
      <Breadcrumb
        crumbs={[
          { label: "首页", to: "/" },
          { label: "电视剧分类", to: "/category" },
          { label: activeTypeName ? `${activeTypeName}剧集` : "全部剧集" },
        ]}
      />

      <div className="mt-4 mb-6">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
          {activeTypeName ? `${activeTypeName}电视剧合集` : "电视剧分类"}
        </h1>
        <p className="mt-2 max-w-3xl text-sm md:text-base leading-relaxed text-foreground-600">
          按类型、地区与年份自由组合筛选，支持按更新时间和热度排序，快速纵览你想看的剧集。
          {hasFilters && <span className="ml-1 text-primary-600">当前已应用筛选条件。</span>}
        </p>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => syncType([])}
          className={`rounded-full px-4 py-1.5 text-sm whitespace-nowrap cursor-pointer transition-colors ${
            types.length === 0 ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-700 hover:bg-background-200"
          }`}
        >
          全部
        </button>
        {dramaTypes.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => syncType(types.includes(t) ? types.filter((v) => v !== t) : [...types, t])}
            className={`rounded-full px-4 py-1.5 text-sm whitespace-nowrap cursor-pointer transition-colors ${
              types.includes(t) ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-700 hover:bg-background-200"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="rounded-lg border border-background-200 bg-background-50">
        <button
          type="button"
          onClick={() => setFiltersOpen((v) => !v)}
          className="flex w-full items-center justify-between px-4 py-3 lg:cursor-default"
        >
          <span className="flex items-center gap-2 text-sm font-medium text-foreground-900">
            <i className="ri-filter-3-line text-base text-primary-500"></i>
            筛选与排序
            {hasFilters && (
              <span className="rounded-full bg-primary-100 px-2 py-0.5 font-label text-xs text-primary-700">
                {types.length + regions.length + years.length}
              </span>
            )}
          </span>
          <i className={`ri-arrow-down-s-line text-lg text-foreground-500 lg:hidden ${filtersOpen ? "rotate-180" : ""} transition-transform`}></i>
        </button>

        <div className={`${filtersOpen ? "block" : "hidden"} lg:block border-t border-background-200 px-4 py-4 space-y-4`}>
          <FilterRow label="类型">
            {dramaTypes.map((t) => (
              <FilterChip key={t} active={types.includes(t)} onClick={() => toggle(types, t, syncType)}>
                {t}
              </FilterChip>
            ))}
          </FilterRow>
          <FilterRow label="地区">
            {dramaRegions.map((r) => (
              <FilterChip key={r} active={regions.includes(r)} onClick={() => toggle(regions, r, setRegions)}>
                {r}
              </FilterChip>
            ))}
          </FilterRow>
          <FilterRow label="年份">
            {dramaYears.map((y) => (
              <FilterChip key={y} active={years.includes(y)} onClick={() => toggle(years, y, setYears)}>
                {y}
              </FilterChip>
            ))}
          </FilterRow>
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-600">排序</span>
              {sortOptions.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => setSort(o.value as SortValue)}
                  className={`rounded-md px-3 py-1.5 text-sm whitespace-nowrap cursor-pointer transition-colors ${
                    sort === o.value ? "bg-primary-100 text-primary-700" : "text-foreground-600 hover:bg-background-100"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={resetFilters}
              className="flex items-center gap-1 rounded-md border border-background-200 px-3 py-1.5 text-sm text-foreground-700 whitespace-nowrap cursor-pointer hover:border-primary-300 hover:text-primary-600 transition-colors"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>
        </div>
      </div>

      <div className="mt-6 mb-4 flex items-center justify-between">
        <p className="text-sm text-foreground-600">
          共找到 <strong className="font-label text-primary-600">{filtered.length}</strong> 部符合条件的剧集
        </p>
        <p className="hidden sm:block text-sm text-foreground-500">
          第 {page} / {totalPages} 页
        </p>
      </div>

      {loading ? (
        <CardSkeleton count={8} />
      ) : pageItems.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
          {pageItems.map((d) => (
            <DramaCard key={d.id} drama={d} />
          ))}
        </div>
      ) : (
        <EmptyState
          icon="ri-search-2-line"
          title="暂无符合条件的剧集"
          description="试试减少筛选条件，或直接重置筛选，看看更多热门剧集。"
          action={
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
            >
              <i className="ri-refresh-line"></i>
              重置筛选条件
            </button>
          }
        />
      )}

      {!loading && pageItems.length > 0 && (
        <Pagination page={page} totalPages={totalPages} onChange={setPage} />
      )}
    </div>
  );
}

function FilterRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
      <span className="w-12 shrink-0 pt-1 text-sm text-foreground-500">{label}</span>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm whitespace-nowrap cursor-pointer transition-colors ${
        active
          ? "border-primary-300 bg-primary-50 text-primary-700"
          : "border-background-200 text-foreground-700 hover:border-primary-200 hover:bg-background-100"
      }`}
    >
      {active && <i className="ri-check-line text-base"></i>}
      {children}
    </button>
  );
}